import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { corPorIndice, TEXTO_SECUNDARIO, GRADE } from './paletaResultados';

// Barra — horizontal (padrão) ou vertical via `orientacao`. Usada pra
// única/múltipla escolha com mais de 3 itens (ver CardPerguntaResultado.js
// pra regra completa de qual gráfico entra em qual situação) e pra
// frequência de palavra de pergunta aberta.
//
// Identidade das barras vem do próprio rótulo no eixo — não duplica isso
// numa legenda (é tecnicamente 1 série só, "votos"; a cor por barra é só
// ênfase visual, a legenda seria redundante com o eixo).
//
// `colors` aqui é função com {index}, NÃO array — de propósito, diferente
// de GraficoPizza.js/GraficoFunil.js/GraficoBolhas.js. No Nivo Bar, um array
// em `colors` mapeia por SÉRIE (cada chave de `keys`), não por barra — como
// só existe 1 série ("votos"), um array pintaria toda barra da mesma cor
// (foi exatamente esse bug quando padronizei os quatro gráficos pro mesmo
// formato). Cada tipo de gráfico do Nivo tem sua própria regra de cor; não
// dá pra copiar a mesma solução pra todos sem checar a documentação de cada um.
// Rótulo do eixo rotacionado (layout vertical): palavra longa demais e a
// caixa de texto girada estoura o viewBox do SVG (que recorta por padrão) —
// aqui ela vazava pra fora do card, cortada no meio. Trunca com reticências;
// o texto completo continua acessível via tooltip ao passar o mouse na barra.
const MAX_CHARS_ROTULO_EIXO = 14;
function truncarRotuloEixo(valor) {
  const texto = String(valor);
  return texto.length > MAX_CHARS_ROTULO_EIXO ? `${texto.slice(0, MAX_CHARS_ROTULO_EIXO - 1)}…` : texto;
}

const GraficoBarras = ({ itens, totalRespostas, rotuloValor = 'voto(s)', orientacao = 'horizontal' }) => {
  if (!itens || itens.length === 0) {
    return <p className="text-sm text-gray-400 italic py-6 text-center">Nenhum dado registrado ainda.</p>;
  }

  const vertical = orientacao === 'vertical';
  const ordenados = [...itens].sort((a, b) => b.votos - a.votos);
  const dados = ordenados.map((item) => ({
    id: item.slug,
    label: item.label,
    votos: item.votos,
    percentual: totalRespostas > 0 ? Math.round((item.votos / totalRespostas) * 1000) / 10 : 0
  }));

  const altura = vertical ? 300 : Math.max(160, dados.length * 52);

  return (
    <div style={{ height: altura }}>
      <ResponsiveBar
        data={dados}
        keys={['votos']}
        indexBy="label"
        layout={vertical ? 'vertical' : 'horizontal'}
        margin={vertical ? { top: 16, right: 16, bottom: 70, left: 56 } : { top: 4, right: 44, bottom: 4, left: 150 }}
        padding={0.35}
        borderRadius={4}
        colors={({ index }) => corPorIndice(index)}
        axisTop={null}
        axisRight={null}
        axisBottom={vertical ? { tickSize: 0, tickPadding: 10, tickRotation: dados.length > 4 ? -30 : 0, format: truncarRotuloEixo } : null}
        axisLeft={vertical ? { tickSize: 0, tickPadding: 8 } : { tickSize: 0, tickPadding: 10 }}
        enableGridX={!vertical}
        enableGridY={vertical}
        gridXValues={!vertical ? 4 : undefined}
        gridYValues={vertical ? 4 : undefined}
        theme={{
          grid: { line: { stroke: GRADE, strokeWidth: 1 } },
          axis: { ticks: { text: { fill: TEXTO_SECUNDARIO, fontSize: 12 } } },
          labels: { text: { fontSize: 12, fontWeight: 600 } }
        }}
        label={(d) => (vertical ? d.data.votos.toLocaleString('pt-BR') : `${d.data.votos.toLocaleString('pt-BR')} (${d.data.percentual}%)`)}
        labelSkipWidth={vertical ? 0 : 40}
        labelSkipHeight={vertical ? 16 : 0}
        labelTextColor={{ from: 'color', modifiers: [['darker', 2.6]] }}
        animate
        motionConfig="gentle"
        role="img"
        ariaLabel="Gráfico de barras com o total de votos por opção"
        tooltip={({ data, color }) => (
          <div className="bg-white shadow-lg rounded-lg pl-2.5 pr-3.5 py-2 border border-gray-100 flex items-stretch gap-2.5">
            <span className="w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <div>
              <p className="text-base font-semibold text-gray-900 leading-tight">
                {data.votos.toLocaleString('pt-BR')} <span className="text-xs font-normal text-gray-400">{rotuloValor}</span>
              </p>
              <p className="text-xs text-gray-500">{data.label} · {data.percentual}%</p>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default GraficoBarras;
